'use client';

import LocaleLink from '@client/components/LocaleLink';
import { ArrowRight, Sparkles } from 'lucide-react';
import { HUB_COPY, type Solution } from './_content';
import type { MarketingLocale } from '@shared/locale';

/**
 * The /solutions hub.
 *
 * Copy comes from the server as a prop rather than from the translation
 * context, because these pages are new and their text lives in `_content.ts`
 * (see the reasoning there). That also keeps the whole page server-rendered:
 * the cards are in the initial HTML, not assembled after hydration.
 */
export default function SolutionsHubContent({
  locale,
  solutions,
}: {
  locale: MarketingLocale;
  solutions: Solution[];
}) {
  const copy = HUB_COPY[locale];

  return (
    <div data-ra-landing className="min-h-screen bg-slate-50 dark:bg-[#080a0f]">
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20 text-orange-700 dark:text-orange-400 text-xs font-semibold mb-4">
            <Sparkles size={12} /> {copy.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            {copy.title}
          </h1>
          <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
            {copy.lede}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {solutions.map((solution) => (
            <LocaleLink
              key={solution.slug}
              href={`/solutions/${solution.slug}`}
              className="group rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 transition-all hover:border-orange-200 dark:hover:border-orange-900/40 hover:shadow-md"
            >
              <div className="text-2xl mb-3" aria-hidden="true">
                {solution.icon}
              </div>
              <h2 className="font-bold text-slate-900 dark:text-white mb-2 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                {solution.title}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                {solution.lede}
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 dark:text-orange-400">
                {copy.readMore}
                <ArrowRight
                  size={14}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </span>
            </LocaleLink>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-center">
          <h2 className="font-bold text-slate-900 dark:text-white mb-2">{copy.ctaTitle}</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-xl mx-auto">
            {copy.ctaBody}
          </p>
          <LocaleLink
            href="/contact"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors"
          >
            {copy.ctaLink} <ArrowRight size={14} />
          </LocaleLink>
        </div>
      </section>
    </div>
  );
}
